/**
 * fa_16 — Social History (14th Ave new-student intake) parent fields.
 *
 * Information-gathering only: no signature, no wet-ink, no consent. Submit is
 * gated on (a) a completion-acknowledgment checkbox rendered by the page, and
 * (b) every Yes/No question answered. Narratives, short text fields, and the
 * independent-checkbox grids (Now/Past conditions, dressing, emotional
 * behavior, habits, drinking/eating, communicate-verbally/gestures) are all
 * optional — a parent leaves any of them blank and still continues.
 *
 * Yes/No controls render as a pair of MUI Checkboxes, mutually exclusive,
 * with three valid states: 'yes', 'no', '' (unanswered, the default). The
 * required Y/N keys are listed in parentPacketValidation.FA16_REQUIRED_YESNO_KEYS;
 * validateForSubmit pushes a per-key error against packetFieldId(key) so the
 * existing Alert + scroll-to-error UX lands on the first unanswered pair.
 */
import React from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { packetFieldId } from './parentPacketValidation';

const CONDITIONS = [
  { key: 'allergies', label: 'Allergies' },
  { key: 'diabetes', label: 'Diabetes' },
  { key: 'sucking', label: 'Sucking difficulty' },
  { key: 'choking', label: 'Choking' },
  { key: 'weight_loss', label: 'Weight loss' },
  { key: 'dehydration', label: 'Dehydration' },
  { key: 'cleft_palate', label: 'Cleft palate' },
  { key: 'breathing', label: 'Breathing difficulty' },
  { key: 'cardiac', label: 'Cardiac problems' },
  { key: 'nasal_regurg', label: 'Nasal regurgitation' },
  { key: 'middle_ear', label: 'Middle ear infection' },
  { key: 'upper_resp', label: 'Upper respiratory infection' },
];

const GARMENTS = [
  { key: 'coat', label: 'Coat' },
  { key: 'shirt', label: 'Shirt' },
  { key: 'pants', label: 'Pants' },
  { key: 'underwear', label: 'Underwear' },
  { key: 'socks', label: 'Socks' },
  { key: 'shoes', label: 'Shoes' },
];

const FASTENERS = [
  { key: 'zip', label: 'Zip' },
  { key: 'button', label: 'Button' },
  { key: 'lace', label: 'Lace' },
  { key: 'tie', label: 'Tie' },
  { key: 'snap', label: 'Snap' },
];

const EMOTIONAL_BEHAVIORS = [
  { key: 'unaware', label: 'Unaware' },
  { key: 'tantrums', label: 'Tantrums' },
  { key: 'bites_himself', label: 'Bites himself' },
  { key: 'bangs_head', label: 'Bangs head' },
  { key: 'passive', label: 'Passive' },
  { key: 'stubborn', label: 'Stubborn' },
  { key: 'destructive', label: 'Destructive' },
  { key: 'hits_himself', label: 'Hits himself' },
  { key: 'shy', label: 'Shy' },
  { key: 'bites_others', label: 'Bites others' },
  { key: 'hyperactive', label: 'Hyperactive' },
  { key: 'easily_upset', label: 'Easily upset' },
  { key: 'hits_others', label: 'Hits others' },
  { key: 'kicks_others', label: 'Kicks others' },
  { key: 'short_attention', label: 'Short attention span' },
  { key: 'jealous', label: 'Jealous' },
  { key: 'happy', label: 'Happy' },
  { key: 'smiles', label: 'Smiles' },
  { key: 'spits', label: 'Spits' },
  { key: 'smiles_socially', label: 'Smiles socially' },
];

const HABITS = [
  { key: 'thumb_sucking', label: 'Thumb sucking' },
  { key: 'rocking', label: 'Rocking' },
  { key: 'drooling', label: 'Drooling' },
  { key: 'finger_play', label: 'Finger play' },
  { key: 'other', label: 'Other' },
];

/**
 * Mutually exclusive Yes/No control. value ∈ {'yes','no',''}; if includeNa is
 * true, also 'na'. Second click on the selected option clears back to ''. This
 * is the standard Yes/No pattern across fa_16 / fa_17 — only one question on
 * fa_16 (Communication → "Uses sign language") opts into the third N/A option.
 */
function YesNoPair({
  label, fieldKey, value, onChange, error, includeNa = false,
}) {
  const set = (next) => onChange(value === next ? '' : next);
  return (
    <Box
      id={packetFieldId(fieldKey)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        flexWrap: 'wrap',
        ...(error
          ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, my: 0.25 }
          : {}),
      }}
    >
      <Typography variant="body2" sx={{ flex: '1 1 60%', minWidth: 220 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={value === 'yes'}
              onChange={() => set('yes')}
              color={error ? 'error' : 'primary'}
            />
          )}
          label="Yes"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={value === 'no'}
              onChange={() => set('no')}
              color={error ? 'error' : 'primary'}
            />
          )}
          label="No"
        />
        {includeNa && (
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={value === 'na'}
                onChange={() => set('na')}
                color={error ? 'error' : 'primary'}
              />
            )}
            label="N/A"
          />
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ width: '100%' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

/** Section header. */
function SectionHeader({ roman, title }) {
  return (
    <Box sx={{ mt: 3, mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {roman}. {title}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

function SubHeader({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
      {children}
    </Typography>
  );
}

export default function Fa16SocialHistoryFields({
  fields, setF, packet, fieldErrors = {}, clearFieldError,
}) {
  const ynValue = (key) => fields[key] ?? '';
  const ynError = (key) => fieldErrors[key];
  const setYn = (key) => (next) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, next);
  };

  const text = (key) => fields[key] ?? '';
  const onText = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.value);
  };
  const onCheck = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.checked);
  };

  const short = (label, key, props = {}) => (
    <TextField
      key={key}
      id={packetFieldId(key)}
      fullWidth
      size="small"
      margin="dense"
      label={label}
      value={text(key)}
      onChange={onText(key)}
      {...props}
    />
  );

  const narrative = (label, key, helperText) => (
    <TextField
      key={key}
      id={packetFieldId(key)}
      fullWidth
      size="small"
      margin="dense"
      label={label}
      value={text(key)}
      onChange={onText(key)}
      multiline
      minRows={3}
      helperText={helperText}
    />
  );

  const checkbox = (label, key) => (
    <FormControlLabel
      key={key}
      control={(
        <Checkbox
          size="small"
          checked={!!fields[key]}
          onChange={onCheck(key)}
        />
      )}
      label={label}
    />
  );

  const yn = (label, key) => (
    <YesNoPair
      key={key}
      label={label}
      fieldKey={key}
      value={ynValue(key)}
      onChange={setYn(key)}
      error={ynError(key)}
    />
  );

  const ynNa = (label, key) => (
    <YesNoPair
      key={key}
      label={label}
      fieldKey={key}
      value={ynValue(key)}
      onChange={setYn(key)}
      error={ynError(key)}
      includeNa
    />
  );

  const childFullName = `${packet?.student_first_name || ''} ${packet?.student_last_name || ''}`.trim();
  const childDob = packet?.student_dob || '';

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Please tell us about your child in your own words. Take as much space as you
        need for any answer — the more you tell us, the better we can support your
        child. You can leave most questions blank if they don&rsquo;t apply, but the
        Yes/No questions must be answered.
      </Typography>

      {short('Person completing this form', 'person_completing_form')}

      <SectionHeader roman="I" title="Identification" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Child:</strong> {childFullName || '—'}{childDob ? ` (DOB ${childDob})` : ''}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel id="fa16-child-sex-label">Child sex</InputLabel>
            <Select
              labelId="fa16-child-sex-label"
              label="Child sex"
              value={text('child_sex')}
              onChange={(e) => {
                if (clearFieldError) clearFieldError('child_sex');
                setF('child_sex', e.target.value);
              }}
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              <MenuItem value="M">M</MenuItem>
              <MenuItem value="F">F</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>{short('Phone', 'phone')}</Grid>
        <Grid item xs={12} sm={8}>{short('Home address', 'address')}</Grid>
        <Grid item xs={6} sm={2}>{short('Apt', 'apt')}</Grid>
        <Grid item xs={6} sm={2}>{short('Zip', 'zip')}</Grid>
        <Grid item xs={12} sm={6}>{short('Agency name', 'agency_name')}</Grid>
        <Grid item xs={12} sm={6}>{short('Social worker', 'social_worker')}</Grid>
      </Grid>
      {short('Other household members', 'household_members')}
      <SubHeader>Emergency contact</SubHeader>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>{short('Name', 'emergency_contact_name')}</Grid>
        <Grid item xs={12} sm={6}>{short('Relationship', 'emergency_contact_relationship')}</Grid>
        <Grid item xs={12} sm={8}>{short('Address', 'emergency_contact_address')}</Grid>
        <Grid item xs={12} sm={4}>{short('Phone', 'emergency_contact_phone')}</Grid>
      </Grid>
      <SubHeader>Parent&rsquo;s employer</SubHeader>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>{short('Employer', 'parent_employer')}</Grid>
        <Grid item xs={12} sm={6}>{short('Employer phone', 'parent_employer_phone')}</Grid>
        <Grid item xs={12}>{short('Employer address', 'parent_employer_address')}</Grid>
      </Grid>

      <SectionHeader roman="II" title="History — Previous Schooling" />
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>{short('Previous school / program name', 'prev_school_name')}</Grid>
        <Grid item xs={12} sm={6}>{short('Location', 'prev_school_location')}</Grid>
        <Grid item xs={12} sm={6}>{short('Phone', 'prev_school_phone')}</Grid>
        <Grid item xs={12} sm={6}>{short('Contact person', 'prev_school_contact')}</Grid>
        <Grid item xs={12} sm={6}>{short('Number of years in attendance', 'prev_school_years')}</Grid>
      </Grid>
      {narrative('What was the focus of the training / program?', 'prev_school_focus')}
      {narrative('Reason for termination or referral to us', 'prev_school_reason_termination')}

      <SectionHeader roman="III" title="Family Structure" />
      {narrative(
        'Who does your child relate to best at home, and how do they show it?',
        'relates_best_at_home',
      )}
      {narrative(
        'Is your child accepted by other children and others in the home? Tell us about it.',
        'child_acceptance',
      )}
      <SubHeader>Who disciplines the child at home</SubHeader>
      {narrative('Who disciplines your child at home?', 'discipline_who')}
      {narrative('1. How is your child disciplined?', 'discipline_how')}
      {narrative("2. What is your child's reaction to discipline?", 'discipline_child_reaction')}
      {short('What language is spoken at home?', 'home_language')}

      <SectionHeader roman="IV" title="Medical Information" />
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>{short('Medical facility / doctor', 'medical_facility')}</Grid>
        <Grid item xs={12} sm={6}>{short('Phone', 'medical_facility_phone')}</Grid>
        <Grid item xs={12} sm={6}>{short('Date of most recent visit', 'medical_recent_visit')}</Grid>
        <Grid item xs={12} sm={6}>{short('Social worker / doctor at clinic', 'medical_clinic_social_worker')}</Grid>
        <Grid item xs={12} sm={6}>{short('Agency name', 'medical_agency_name')}</Grid>
        <Grid item xs={12} sm={6}>{short('Social worker', 'medical_social_worker')}</Grid>
      </Grid>
      <SubHeader>Medication</SubHeader>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>{short('Medication name', 'medication_name')}</Grid>
        <Grid item xs={12} sm={6}>{short('Date prescribed', 'medication_date_prescribed')}</Grid>
        <Grid item xs={12} sm={6}>{short('Reason / condition', 'medication_reason')}</Grid>
        <Grid item xs={12} sm={6}>{short('Dosage', 'medication_dosage')}</Grid>
        <Grid item xs={12} sm={6}>{short('Time(s) of day', 'medication_time')}</Grid>
        <Grid item xs={12} sm={6}>{short('Date last evaluated', 'medication_last_eval')}</Grid>
      </Grid>
      <SubHeader>Seizures</SubHeader>
      {yn('Does your child have seizures?', 'seizures_yn')}
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>{short('Type', 'seizures_type')}</Grid>
        <Grid item xs={12} sm={4}>{short('Signs of onset', 'seizures_onset_signs')}</Grid>
        <Grid item xs={12} sm={4}>{short('Seizure pattern', 'seizures_pattern')}</Grid>
      </Grid>
      {narrative('Reason for any hospitalization and how long', 'hospitalization_reason')}
      {narrative('Specific handicaps or diagnoses', 'specific_handicaps')}
      {narrative(
        'Previous medical association (clinic name / location / dates)',
        'prev_medical_assoc',
      )}

      <SectionHeader roman="V" title="Conditions — Now and Past" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Check any of the following your child has now (Now) and any your child has
        had in the past (Past). Both columns can be checked for the same condition.
      </Typography>
      <Box sx={{ mb: 1 }}>
        <Grid container spacing={0.5} alignItems="center">
          <Grid item xs={6} />
          <Grid item xs={3}><Typography variant="caption" sx={{ fontWeight: 700 }}>Now</Typography></Grid>
          <Grid item xs={3}><Typography variant="caption" sx={{ fontWeight: 700 }}>Past</Typography></Grid>
          {CONDITIONS.map((c) => (
            <React.Fragment key={c.key}>
              <Grid item xs={6}>
                <Typography variant="body2">{c.label}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Checkbox
                  size="small"
                  checked={!!fields[`cond_${c.key}_now`]}
                  onChange={onCheck(`cond_${c.key}_now`)}
                />
              </Grid>
              <Grid item xs={3}>
                <Checkbox
                  size="small"
                  checked={!!fields[`cond_${c.key}_past`]}
                  onChange={onCheck(`cond_${c.key}_past`)}
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
      {narrative(
        'If your child has had upper respiratory or middle ear infections, how often and how severe?',
        'infections_freq_severity',
      )}

      <SectionHeader roman="VI" title="Self Help" />
      <SubHeader>Toileting</SubHeader>
      <YesNoPair
        key="toileting_assisted"
        label="Is toileting assisted or unassisted?"
        fieldKey="toileting_assisted"
        value={text('toileting_assisted')}
        onChange={(next) => {
          if (clearFieldError) clearFieldError('toileting_assisted');
          setF('toileting_assisted', next);
        }}
        error={fieldErrors.toileting_assisted}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 0.5 }}>
        (Above: check Assisted for &ldquo;Yes&rdquo;, Unassisted for &ldquo;No&rdquo; — or leave both unchecked.)
      </Typography>
      {narrative('Toileting schedule', 'toileting_schedule')}
      {narrative('Frequency of accidents', 'toileting_accident_frequency')}
      {narrative(
        'How often does your child indicate the need to go to the bathroom?',
        'toileting_indicates_need',
      )}
      {narrative(
        'How does your child adjust clothing for toileting?',
        'toileting_adjust_clothing',
      )}

      <SubHeader>Dressing</SubHeader>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Check what your child can take off and put on (both can be checked).
      </Typography>
      <Box>
        <Grid container spacing={0.5} alignItems="center">
          <Grid item xs={6} />
          <Grid item xs={3}><Typography variant="caption" sx={{ fontWeight: 700 }}>Takes off</Typography></Grid>
          <Grid item xs={3}><Typography variant="caption" sx={{ fontWeight: 700 }}>Puts on</Typography></Grid>
          {GARMENTS.map((g) => (
            <React.Fragment key={g.key}>
              <Grid item xs={6}><Typography variant="body2">{g.label}</Typography></Grid>
              <Grid item xs={3}>
                <Checkbox
                  size="small"
                  checked={!!fields[`dressing_takes_off_${g.key}`]}
                  onChange={onCheck(`dressing_takes_off_${g.key}`)}
                />
              </Grid>
              <Grid item xs={3}>
                <Checkbox
                  size="small"
                  checked={!!fields[`dressing_puts_on_${g.key}`]}
                  onChange={onCheck(`dressing_puts_on_${g.key}`)}
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Does your child:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {FASTENERS.map((f) => checkbox(f.label, `dressing_${f.key}`))}
      </Box>
      {narrative('Other information about dressing', 'dressing_other_info')}

      <SubHeader>Cleanliness</SubHeader>
      {narrative(
        'Tell us how your child manages washing, bathing, and grooming — what they can do on their own and what they need help with.',
        'cleanliness_notes',
      )}

      <SubHeader>Sleep Habits</SubHeader>
      {short('Bedtime', 'sleep_bedtime')}
      {yn('Does your child take a nap?', 'sleep_takes_nap')}
      {yn('Does your child sleep through the night?', 'sleep_through_night')}
      {short('Where and with whom does your child sleep?', 'sleep_where_with_whom')}
      {yn('Does your child have toilet accidents at night?', 'sleep_toilet_accidents')}
      {narrative(
        'Nightmares or other peculiarities? (Tell us what you notice.)',
        'sleep_nightmares',
      )}

      <SubHeader>Communication</SubHeader>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        How does your child communicate? (Both can apply.)
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {checkbox('Verbally', 'comm_verbally')}
        {checkbox('Gestures', 'comm_gestures')}
      </Box>
      {yn('Does your child speak using phrases?', 'comm_phrases')}
      {yn('Does your child speak using sentences?', 'comm_sentences')}
      {yn('Does your child follow simple directions?', 'comm_directions')}
      {yn('Does your child respond to their name?', 'comm_responds_name')}
      {ynNa('Does your child use sign language?', 'comm_sign_language')}
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          {short('Speech therapy outside school — provider', 'comm_speech_therapy_provider')}
        </Grid>
        <Grid item xs={12} sm={6}>
          {short('How often?', 'comm_speech_therapy_frequency')}
        </Grid>
      </Grid>

      <SectionHeader roman="VII" title="Skills & Emotional Behavior" />
      {narrative('How does your child use leisure time?', 'leisure_time')}
      {narrative('Is there anything your child fears or is upset by?', 'fears_upsetters')}
      {narrative("What are your child's favorite activities?", 'favorite_activities')}
      {yn('Does your child go outside unattended?', 'outside_unattended')}
      <SubHeader>Play habits</SubHeader>
      {yn('Does your child occupy themselves?', 'play_occupies_self')}
      {yn('Does your child play with other children?', 'play_with_other_children')}
      {yn('Does your child play with adults?', 'play_with_adults')}
      {yn('Does your child play appropriately with toys?', 'play_appropriately_toys')}

      <SubHeader>Emotional behavior</SubHeader>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Check any that describe your child (check as many as apply).
      </Typography>
      <Grid container spacing={0}>
        {EMOTIONAL_BEHAVIORS.map((b) => (
          <Grid item xs={6} sm={4} md={3} key={b.key}>
            {checkbox(b.label, `eb_${b.key}`)}
          </Grid>
        ))}
      </Grid>

      <SubHeader>Habits</SubHeader>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {HABITS.map((h) => checkbox(h.label, `habit_${h.key}`))}
      </Box>
      {fields.habit_other && short('Describe the other habit', 'habit_other_text')}

      <SubHeader>Attention &amp; reactions</SubHeader>
      {yn('Does your child seek attention all the time?', 'seeks_attention')}
      {narrative('If yes, how do they seek attention?', 'seeks_attention_how')}
      {narrative('What calms your child?', 'what_calms')}
      {yn('Does your child share attention?', 'shares_attention')}
      {narrative('How does your child react to strangers?', 'reacts_to_strangers')}
      {narrative('How does your child react to people they know?', 'reacts_to_familiar')}
      {narrative('How does your child react to animals?', 'reacts_to_animals')}
      {narrative('How does your child react to new environments?', 'reacts_to_new_environments')}
    </Box>
  );
}

/** Default empty fields for fa_16 — every required Y/N starts unanswered (''). */
export function initialFa16Fields() {
  const init = {};
  const RYN = [
    'seizures_yn',
    'sleep_takes_nap', 'sleep_through_night', 'sleep_toilet_accidents',
    'comm_phrases', 'comm_sentences', 'comm_directions', 'comm_responds_name', 'comm_sign_language',
    'outside_unattended',
    'play_occupies_self', 'play_with_other_children', 'play_with_adults', 'play_appropriately_toys',
    'seeks_attention', 'shares_attention',
  ];
  for (const k of RYN) init[k] = '';
  return init;
}
